import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from '@angular/core';
import { HttpClient } from '@angular/common/http';

declare var DBSService_BASE_URL: string;

export interface MarketingSheetPhoto {
  slot: 'top' | 'side';
  label: string;
  imageUrl?: string;
  isLoading: boolean;
  error?: string;
  fileName?: string;
}

@Component({
  selector: 'marketing-sheet-photos',
  standalone: false,
  templateUrl: './marketing-sheet-photos.component.html',
  styleUrls: ['./marketing-sheet-photos.component.css']
})
export class MarketingPhotosComponent implements OnInit, OnChanges, OnDestroy {

  /** Material number (SAP part number) — used to look up photos via BitImage API */
  @Input({ required: true }) materialNumber!: string;

  /**
   * Sub-PSL name (e.g. 'FixedCutter', 'RollerCone').
   * Passed as the `subpsl` query param to the API.
   */
  @Input({ required: true }) subPslName!: string;

  /** Show a header with title and actions */
  @Input() showHeader = true;

  /** Header title */
  @Input() title = 'Marketing Sheet Photos';

  /** Allow uploading new / replacement photos */
  @Input() allowUpload = true;

  /** Emitted when a photo is clicked */
  @Output() photoClicked = new EventEmitter<MarketingSheetPhoto>();

  /** Emitted after new photos have been uploaded and the display refreshed */
  @Output() photosUploaded = new EventEmitter<void>();

  photos: MarketingSheetPhoto[] = [];
  isLoading = false;
  showUploader = false;
  viewerPhoto: MarketingSheetPhoto | null = null;

  /** Files selected for upload — one per slot */
  uploadFiles: { top: File | null; side: File | null } = { top: null, side: null };
  isUploading = false;

  /** Blob-URL cleanup list */
  private objectUrls: string[] = [];

  private get baseUrl(): string {
    return DBSService_BASE_URL;
  }

  /** Map subPslName to API query param value */
  private get subPslParam(): string {
    const name = (this.subPslName || '').toLowerCase().replace(/\s/g, '');
    if (name.includes('rollercone') || name === 'rc') return 'rc';
    return 'fc';
  }

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.initSlots();
    if (this.materialNumber && this.subPslName) {
      this.loadPhoto();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const materialChanged = changes['materialNumber'] && !changes['materialNumber'].firstChange;
    const subPslChanged = changes['subPslName'] && !changes['subPslName'].firstChange;

    if (materialChanged || subPslChanged) {
      this.revokeUrls();
      this.initSlots();
      if (this.materialNumber && this.subPslName) {
        this.loadPhoto();
      }
    }
  }

  ngOnDestroy(): void {
    this.revokeUrls();
  }

  /** Refresh photos from server */
  refresh(): void {
    this.revokeUrls();
    this.initSlots();
    this.loadPhoto();
  }

  // ── Internal ──

  private initSlots(): void {
    this.photos = [
      { slot: 'side', label: 'Side View', isLoading: false },
      { slot: 'top', label: 'Top View', isLoading: false }
    ];
  }

  /**
   * Downloads both bit images via GET api/BitImage/download/{materialNumber}?subpsl=...&view=...
   * Loads _side.png and _top.png independently.
   */
  private loadPhoto(): void {
    if (!this.materialNumber || !this.subPslName) return;

    this.isLoading = true;

    for (const photo of this.photos) {
      photo.isLoading = true;
      photo.error = undefined;

      const url = `${this.baseUrl}/api/BitImage/download/${encodeURIComponent(this.materialNumber)}?subpsl=${this.subPslParam}&view=${photo.slot}`;

      this.http.get(url, { responseType: 'blob' }).subscribe({
        next: (blob) => {
          if (blob && blob.size > 0 && blob.type !== 'text/plain') {
            const objectUrl = URL.createObjectURL(blob);
            this.objectUrls.push(objectUrl);
            photo.imageUrl = objectUrl;
            photo.fileName = `${this.materialNumber}_${photo.slot}.png`;
          }
          photo.isLoading = false;
          this.updateGlobalLoading();
        },
        error: (err) => {
          console.error(`Failed to download ${photo.slot} bit image:`, err);
          photo.isLoading = false;
          photo.error = err.status === 404
            ? `No ${photo.label.toLowerCase()} found for this material number.`
            : 'Failed to load photo. Please try again.';
          this.updateGlobalLoading();
        }
      });
    }
  }

  /** Update global loading flag when all individual slots finish */
  private updateGlobalLoading(): void {
    this.isLoading = this.photos.some(p => p.isLoading);
  }

  // ── Photo actions ──

  onPhotoClick(photo: MarketingSheetPhoto): void {
    this.photoClicked.emit(photo);
    if (photo.imageUrl) {
      this.viewerPhoto = photo;
    }
  }

  closeViewer(): void {
    this.viewerPhoto = null;
  }

  downloadPhoto(photo: MarketingSheetPhoto): void {
    if (!photo.imageUrl) return;

    const url = `${this.baseUrl}/api/BitImage/download/${encodeURIComponent(this.materialNumber)}?subpsl=${this.subPslParam}&view=${photo.slot}`;

    this.http.get(url, { responseType: 'blob' }).subscribe((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = photo.fileName || `${this.materialNumber}_${photo.slot}.png`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    });
  }

  retryPhoto(photo: MarketingSheetPhoto): void {
    photo.error = undefined;
    this.loadPhoto();
  }

  // ── Upload integration ──

  toggleUploadMode(): void {
    this.showUploader = !this.showUploader;
    if (!this.showUploader) {
      this.uploadFiles = { top: null, side: null };
    }
  }

  onFileSelected(event: Event, slot: 'top' | 'side'): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.uploadFiles[slot] = input.files[0];
    }
  }

  hasFilesToUpload(): boolean {
    return !!(this.uploadFiles.top || this.uploadFiles.side);
  }

  uploadPhotos(): void {
    if (!this.hasFilesToUpload() || !this.materialNumber) return;

    this.isUploading = true;
    let remaining = 0;
    let hasError = false;

    const filesToUpload: { file: File; slot: 'top' | 'side' }[] = [];
    if (this.uploadFiles.top) filesToUpload.push({ file: this.uploadFiles.top, slot: 'top' });
    if (this.uploadFiles.side) filesToUpload.push({ file: this.uploadFiles.side, slot: 'side' });
    remaining = filesToUpload.length;

    for (const item of filesToUpload) {
      const formData = new FormData();

      // Rename the file to match the expected naming convention
      const extension = item.file.name.split('.').pop() || 'png';
      const renamedFileName = `${this.materialNumber}_${item.slot}.${extension}`;
      const renamedFile = new File([item.file], renamedFileName, { type: item.file.type });

      formData.append('file', renamedFile, renamedFileName);

      const url = `${this.baseUrl}/api/BitImage/upload/${encodeURIComponent(this.materialNumber)}?overwrite=true&subpsl=${this.subPslParam}`;

      this.http.post(url, formData).subscribe({
        next: () => {
          remaining--;
          if (remaining === 0) {
            this.isUploading = false;
            this.showUploader = false;
            this.uploadFiles = { top: null, side: null };
            this.photosUploaded.emit();
            // Refresh after a short delay
            setTimeout(() => this.refresh(), 1500);
          }
        },
        error: (err) => {
          console.error(`Failed to upload ${item.slot} photo:`, err);
          hasError = true;
          remaining--;
          if (remaining === 0) {
            this.isUploading = false;
            if (hasError) {
              this.photos.forEach(p => {
                if (!p.imageUrl) p.error = 'Upload failed. Please try again.';
              });
            }
          }
        }
      });
    }
  }

  // ── Cleanup ──

  private revokeUrls(): void {
    for (const url of this.objectUrls) {
      URL.revokeObjectURL(url);
    }
    this.objectUrls = [];
  }
}
